// DualWaveformVisualizer.js - Updated with AudioContext resume

import React, { useEffect, useRef } from 'react';

const DualWaveformVisualizer = ({ userStream, aiAudio, isUserSpeaking, isAiSpeaking, audioContext }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const userAnalyserRef = useRef(null);
    const aiAnalyserRef = useRef(null);
    const userSourceRef = useRef(null);
    const aiSourceRef = useRef(null);


    // const USER_COLOR = '#00bbff'; // Currently not used
    const AI_COLOR ='#ffc300';
    const BG_COLOR = '#fff';



    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const canvasHeight = canvas.height;
        const canvasWidth = canvas.width;

        // Only initialize audio analyzers if we have an audioContext and it's available
        if (!audioContext || audioContext.state === 'suspended' || audioContext.state === 'closed' || audioContext.state === 'interrupted') {
            // Just draw idle waves if no audio context or context has issues
            const draw = () => {
                ctx.fillStyle = BG_COLOR;
                ctx.fillRect(0, 0, canvasWidth, canvasHeight);
                ctx.beginPath();
                ctx.strokeStyle = '#fff';
                ctx.lineWidth = 0.1;
                ctx.moveTo(0, canvasHeight / 2);
                ctx.lineTo(canvasWidth, canvasHeight / 2);
                ctx.stroke();

                drawIdleWave(ctx, canvasHeight / 2, canvasHeight / 2, canvasWidth, '#fff');
                animationRef.current = requestAnimationFrame(draw);
            };
            
            const drawIdleWave = (ctx, startY, height, width, color) => {
                ctx.lineWidth = 1;
                ctx.strokeStyle = color;
                ctx.globalAlpha = 1;
                ctx.beginPath();
                const centerY = startY + height / 2;
                ctx.moveTo(0, centerY);
                for (let x = 0; x < width; x++) {
                    const y = centerY + Math.sin(x * 0.02) * 2;
                    ctx.lineTo(x, y);
                }
                ctx.stroke();
                ctx.globalAlpha = 1;
            };

            draw();
            return () => cancelAnimationFrame(animationRef.current);
        }

        try {
            if (!userAnalyserRef.current) {
                userAnalyserRef.current = audioContext.createAnalyser();
                userAnalyserRef.current.fftSize = 2048;
            }

            if (!aiAnalyserRef.current) {
                aiAnalyserRef.current = audioContext.createAnalyser();
                aiAnalyserRef.current.fftSize = 2048;
            }
        } catch (error) {
            console.error('Error creating audio analysers:', error);
            // Fall back to idle wave display
            const draw = () => {
                ctx.fillStyle = BG_COLOR;
                ctx.fillRect(0, 0, canvasWidth, canvasHeight);
                drawIdleWave(ctx, canvasHeight / 2, canvasHeight / 2, canvasWidth, '#fff');
                animationRef.current = requestAnimationFrame(draw);
            };
            
            const drawIdleWave = (ctx, startY, height, width, color) => {
                ctx.lineWidth = 1;
                ctx.strokeStyle = color;
                ctx.beginPath();
                const centerY = startY + height / 2;
                ctx.moveTo(0, centerY);
                for (let x = 0; x < width; x++) {
                    const y = centerY + Math.sin(x * 0.02) * 2;
                    ctx.lineTo(x, y);
                }
                ctx.stroke();
            };
            
            draw();
            return () => cancelAnimationFrame(animationRef.current);
        }

        // Connect user stream if available
        if (userStream && isUserSpeaking) {
            try {
                if (userSourceRef.current) {
                    userSourceRef.current.disconnect();
                }
                userSourceRef.current = audioContext.createMediaStreamSource(userStream);
                userSourceRef.current.connect(userAnalyserRef.current);
                console.log('User stream connected to analyser');
            } catch (error) {
                console.error('Error connecting user stream:', error);
                // Fall back to idle wave if connection fails
                userSourceRef.current = null;
            }
        }

        // Connect AI audio if available
        if (aiAudio && isAiSpeaking) {
            try {
                if (aiSourceRef.current) {
                    aiSourceRef.current.disconnect();
                }
                if (aiAudio instanceof HTMLAudioElement) {
                    // Check if the audio element is already connected to avoid duplicate connections
                    aiSourceRef.current = audioContext.createMediaElementSource(aiAudio);
                    aiSourceRef.current.connect(aiAnalyserRef.current);
                    aiAnalyserRef.current.connect(audioContext.destination);
                    console.log('AI audio element connected to analyser');
                }
            } catch (error) {
                console.error('Error connecting AI audio:', error);
                // If we get an error about already being connected, that's okay
                if (error.message.includes('already connected') || error.name === 'InvalidStateError') {
                    console.log('Audio element already connected, continuing...');
                } else {
                    // Fall back to idle wave if connection fails
                    aiSourceRef.current = null;
                }
            }
        }

        // Prepare data arrays for both analysers
        // const userBufferLength = userAnalyserRef.current.fftSize;
        // const userDataArray = new Uint8Array(userBufferLength); // Currently not used
        const aiBufferLength = aiAnalyserRef.current.fftSize;
        const aiDataArray = new Uint8Array(aiBufferLength);

        const draw = () => {
            ctx.fillStyle = BG_COLOR;
            ctx.fillRect(0, 0, canvasWidth, canvasHeight);
            ctx.beginPath();
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = 0.1;
            ctx.moveTo(0, canvasHeight / 2);
            ctx.lineTo(canvasWidth, canvasHeight / 2);
            ctx.stroke();

            // Draw user waveform on top
/*            if (isUserSpeaking && userSourceRef.current) {
                userAnalyserRef.current.getByteTimeDomainData(userDataArray);
                drawWaveform(ctx, userDataArray, 0, canvasHeight / 2, canvasWidth, USER_COLOR);
            } else {
                drawIdleWave(ctx, 0, canvasHeight / 2, canvasWidth, isUserSpeaking ? USER_COLOR : '#fff');
            }*/

            // Draw AI waveform on bottom
            if (isAiSpeaking && aiSourceRef.current) {
                aiAnalyserRef.current.getByteTimeDomainData(aiDataArray);
                drawWaveform(ctx, aiDataArray, canvasHeight / 2, canvasHeight / 2, canvasWidth, AI_COLOR);
            } else {
                drawIdleWave(ctx, canvasHeight / 2, canvasHeight / 2, canvasWidth, isAiSpeaking ? AI_COLOR : '#fff');
            }

            // Add labels
            //ctx.font = '10px Arial';
        //ctx.fillStyle = isUserSpeaking ? USER_COLOR : '#84b718';
        //    ctx.fillText('You', 10, 20);
        //    ctx.fillStyle = isAiSpeaking ? AI_COLOR : '#84b718';
//ctx.fillText('DocMJ Assistant', 10, canvasHeight - 10);

            animationRef.current = requestAnimationFrame(draw);
        };

        const drawWaveform = (ctx, dataArray, startY, height, width, color) => {
            ctx.lineWidth = 1;
            ctx.strokeStyle = color;
            ctx.beginPath();
            const sliceWidth = width / dataArray.length;
            let x = 0;
            for (let i = 0; i < dataArray.length; i++) {
                const v = dataArray[i] / 128.0;
                const y = startY + (v * height / 2);
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
                x += sliceWidth;
            }
            ctx.lineTo(width, startY + height / 2);
            ctx.stroke();
        };

        const drawIdleWave = (ctx, startY, height, width, color) => {
            ctx.lineWidth = 1;
            ctx.strokeStyle = color;
            ctx.globalAlpha = 1;
            ctx.beginPath();
            const centerY = startY + height / 2;
            ctx.moveTo(0, centerY);
            for (let x = 0; x < width; x++) {
                const y = centerY + Math.sin(x * 0.02) * 2;
                ctx.lineTo(x, y);
            }
            ctx.stroke();
            ctx.globalAlpha = 1;
        };

        draw();
        return () => {
            cancelAnimationFrame(animationRef.current);
            if (userSourceRef.current) userSourceRef.current.disconnect();
            if (aiSourceRef.current) aiSourceRef.current.disconnect();
        };
    }, [userStream, aiAudio, isUserSpeaking, isAiSpeaking, audioContext]);

    return (
        <div className="absolute w-full h-full justify-center">
            <canvas
                ref={canvasRef}
                width="700"
                height="100"
                style={{ width: '100%', height: '100px', backgroundColor: BG_COLOR, borderRadius: '8px' }}
                />
{/*            {isUserSpeaking && (
                <div className="absolute top-1 right-2 text-dark  text-xs animate-pulse">
                    listening
                </div>
            )}*///
                }
            {isAiSpeaking && (
                <div className="absolute top-1 right-2 text-dark text-xs animate-pulse">
                    Speaking
                </div>
            )}
        </div>
    );
};

export default DualWaveformVisualizer;
